// ---- the brains ----
// What each animal does in the field: a small state machine, a frame at a time.
// Every state is a key of CLIPOF (animals.ts), every brain ends in settle(),
// which draws the pose for its state, and stepToward() is the walk they share.
// A brain changes state with pause(), enter() and walkTo() (animals.ts), and
// cry() below is the one move every noise makes. BRAINS at the bottom is the
// registry a row in data.ts names its brain by.
import {animalSfx,sndBaa,sndBlub,sndBray,sndCrow,sndHiss,sndHoot,sndMoo,sndQuack,sndTrumpet,sndYip,sndOink,sndSqueak,sndMeow,sndBark,sndCluck,sndGrowl,sndCheep,sndHowl,sndBellow,sndSnort,sndGrunt,sndChirp,sndLaugh,sndScreech,sndCaw,sndCoo,sndSquawk,sndPeng,sndArf,sndWalrus,sndChitter} from './cries';
import {enter,float,pause,settle,stepToward,walkTo} from './animals';
import type {State} from './animals';
import {FIELD,animals,fieldSpot,jiddo} from './field';
import {hushed,jiddoNotice} from './jiddo';
import {bank,crossesWater,onWater,pondR,pondSpot} from '../../core/water';
import {POND as pond} from './set';
import {rnd} from '../../core/util';
import type {Animal,Brain} from './types';

// A cry: the state entered for as long as the noise lasts — the recording's
// own length when there is one, the written beep's otherwise — and Jiddo turns
// round to it. Every brain gates it on !hushed(t): nobody cries over his voice.
const cry=(a:Animal,st:State,t:number,snd:()=>void,ms:number)=>{enter(a,st,t,animalSfx(a,snd,ms));jiddoNotice(a,t);};

// Where an elephant goes to drink: the bank on its own side of the pond, spread
// along the edge, its toes at the water — a spot inside the field that it
// reaches without crossing the pond. Nobody else's spacing is asked for, as it
// never was for the bank: a full herd at the water is a bump, and bumping is
// handled. null when the pond's edge is off the field from where it stands.
export function drinkSpot(a:Animal){
  const p=a.position, th=Math.atan2(p.z-pond.z,p.x-pond.x);
  for(let i=0;i<6;i++){
    const s=bank(th+rnd(-1.1,1.1),a.pad,0.1);
    if(Math.abs(s.x)<=FIELD.x+0.5&&s.z>=FIELD.zFar-0.5&&s.z<=FIELD.zNear&&!crossesWater(p.x,p.z,s.x,s.z,a.pad))return s;
  }
  return null;
}
// the elephant's brain: pause | walk | trumpet | drink
function elephantBrain(a:Animal,t:number,dt:number){
  const age=t-a.stT, p=a.position; let walking=false;
  if(a.st==='trumpet'){if(age>a.stMs){pause(a,t,600,1900);}}
  else if(a.st==='drink'){if(age>1800){pause(a,t,600,1900);}}
  else if(a.st==='pause'){
    if(age>a.wait){
      const r=Math.random(), atPond=pondR(p.x,p.z,a.pad)<1.35;   // a stride or so from the bank
      if(r<0.20&&!hushed(t)){cry(a,'trumpet',t,sndTrumpet,1350);}
      else if(atPond&&r<0.62&&!hushed(t)){a.st='drink';a.stT=t;a.head=Math.atan2(pond.x-p.x,pond.z-p.z);   // the blubs wait for him too
        [500,900,1300].forEach(d=>setTimeout(sndBlub,d));}
      else{
        const s=(Math.random()<0.35&&drinkSpot(a))||fieldSpot(p,a.pad);   // often, the water
        walkTo(a,s,t);
      }
    }
  }else walking=stepToward(a,t,dt);
  settle(a,t,dt,walking);
}
// the donkey's brain: pause | walk | graze | bray
function donkeyBrain(a:Animal,t:number,dt:number){
  const age=t-a.stT; let walking=false;
  if(a.st==='graze'){if(age>2600){pause(a,t,420,1320);}}
  else if(a.st==='bray'){if(age>a.stMs){pause(a,t,420,1320);}}
  else if(a.st==='pause'){
    if(age>a.wait){
      const r=Math.random();
      if(r<0.28){a.st='graze';a.stT=t;}
      else if(r<0.42&&!hushed(t)){cry(a,'bray',t,sndBray,1150);}
      else walkTo(a,fieldSpot(a.position,a.pad),t);
    }
  }else walking=stepToward(a,t,dt);
  settle(a,t,dt,walking);
}
// the cow's brain: pause | walk | graze | moo | rest
// Slower than the donkey and mostly eating, which is what a cow is. Rest is the
// Sit clip held for a few seconds — she sits down in the grass, haunches first
// like a dog, and gets up again — and the moo is the bray's shape: the cry
// fires, she rears up under it (CLIPOF, animals.ts), and Jiddo turns round.
function cowBrain(a:Animal,t:number,dt:number){
  const age=t-a.stT; let walking=false;
  if(a.st==='graze'){if(age>3400){pause(a,t,500,1500);}}
  else if(a.st==='rest'||a.st==='moo'){if(age>a.stMs){pause(a,t,500,1500);}}
  else if(a.st==='pause'){
    if(age>a.wait){
      const r=Math.random();
      if(r<0.38){a.st='graze';a.stT=t;}
      else if(r<0.50&&!hushed(t)){cry(a,'moo',t,sndMoo,1000);}
      else if(r<0.62){enter(a,'rest',t,rnd(4000,7000));}
      else walkTo(a,fieldSpot(a.position,a.pad),t);
    }
  }else walking=stepToward(a,t,dt);
  settle(a,t,dt,walking);
}

// the duck's brain: pause | walk | float | swim | cry | eat
// She is the first bird, and the pond's first resident — the penguin, the sea
// lion and the walrus swim the same way: the checks that keep everyone else
// off the water let a spec with `swims` through, so she walks off the bank and
// keeps going. Once her centre is over the water her walk is a swim and her
// pause a float — the same states with the Swim clip under them and the body
// let down a little — and a step back onto the grass turns them back. Eat is
// her dabbling, on the water or off it; given a rest, she lies on the bank on
// the Sit clip too. The cry is the bray's shape: it fires, her head goes back
// and her wings fly up under it (CLIPOF, animals.ts), and Jiddo turns round.
// A rest, for the animals that have one: how often (p, a share of the roll)
// and how long (ms, a range). One field rather than two, so that a brain
// cannot read how often without first having found the rest is there — and
// so cannot fall into a rest with no length to it, as the herd's once did.
interface Rest{p:number;ms:[number,number]}
interface SwimOpts{snd:()=>void;ms:number;cryP:number;eatP:number;toWater:number;toLand:number;dip:number;cry:State;rest?:Rest}
function swimmer(a:Animal,t:number,dt:number,o:SwimOpts){
  const age=t-a.stT, p=a.position, wet=onWater(p.x,p.z,0); let walking=false;
  a.dip=(a.dip||0)+((wet?o.dip:0)-(a.dip||0))*Math.min(1,4*dt); a.slow=wet?0.7:1;
  if(a.st===o.cry||a.st==='rest'){if(age>a.stMs){pause(a,t,500,1500);}}
  else if(a.st==='eat'){if(age>1600){pause(a,t,500,1500);}}
  else if(a.st==='pause'||a.st==='float'){
    if(age>a.wait){
      const r=Math.random(), rest=wet?undefined:o.rest, restP=rest?rest.p:0;   // she rests on the bank, never afloat
      if(r<o.cryP&&!hushed(t)){cry(a,o.cry,t,o.snd,o.ms);}
      else if(r<o.cryP+o.eatP){a.st='eat';a.stT=t;}
      else if(rest&&r<o.cryP+o.eatP+restP){enter(a,'rest',t,rnd(rest.ms[0],rest.ms[1]));}
      else{                                       // mostly to the water from the grass, sometimes the grass from the water
        const s=Math.random()<(wet?o.toLand:o.toWater)?pondSpot():fieldSpot(wet?null:p,a.pad);
        walkTo(a,s,t,wet?'swim':'walk');
      }
    }
  }else walking=stepToward(a,t,dt);
  // the bank, crossed either way — after the step, so the clip under her never
  // shows a frame of standing between a swim and the float it ends in
  if(a.st==='walk'&&wet)a.st='swim'; else if(a.st==='swim'&&!wet)a.st='walk';
  if(a.st==='pause'&&wet)a.st='float'; else if(a.st==='float'&&!wet)a.st='pause';
  settle(a,t,dt,walking&&a.st==='walk');
}
const DUCK:SwimOpts={snd:sndQuack,ms:900,cryP:0.18,eatP:0.22,toWater:0.6,toLand:0.35,dip:-0.05,cry:'quack'};
function duckBrain(a:Animal,t:number,dt:number){swimmer(a,t,dt,DUCK);}

// the snake's brain: pause | walk | hiss | coil
// No legs at all: a head, a neck and a tail on one body, which the pack's Walk
// clip slithers along. Coil is the Sit clip held for a few seconds, and the
// hiss is the bray's shape: the cry fires, she rears and strikes under it
// (CLIPOF, animals.ts), and Jiddo turns round. Her name is the Levantine word, حية — the one Danny hears at
// home — rather than the book's ثعبان; the picture is filed under both.
function snakeBrain(a:Animal,t:number,dt:number){
  const age=t-a.stT; let walking=false;
  if(a.st==='coil'||a.st==='hiss'){if(age>a.stMs){pause(a,t,500,1600);}}
  else if(a.st==='pause'){
    if(age>a.wait){
      const r=Math.random();
      if(r<0.16&&!hushed(t)){cry(a,'hiss',t,sndHiss,1100);}
      else if(r<0.40){enter(a,'coil',t,rnd(3000,6000));}
      else walkTo(a,fieldSpot(a.position,a.pad),t);
    }
  }else walking=stepToward(a,t,dt);
  settle(a,t,dt,walking);
}

// the tortoise's brain: pause | walk | eat | startle | hide
// The slowest thing in the field by a long way, and the only one that answers
// a bump by vanishing: whoever walks into her — Jiddo included — gets a
// startled hop (the Fear clip, one cycle) and then a tortoise pulled in, head
// down and legs tucked (the Sit clip, held) for a few seconds before she
// carries on. The rig has no head bone, so no clip in the box can draw the
// head into the shell; Sit is the nearest it comes. Now and then she pulls in
// for no reason at all, which is the thing Jiddo remarks on; she is silent
// otherwise, like the rabbit.
function tortoiseBrain(a:Animal,t:number,dt:number){
  if(a.bumped){a.bumped=false;if(a.st!=='hide'&&a.st!=='startle'){enter(a,'startle',t,450);}}
  const age=t-a.stT; let walking=false;                 // after the bump, so the hop is not already old
  if(a.st==='startle'){if(age>a.stMs){enter(a,'hide',t,rnd(2500,4500));}}
  else if(a.st==='hide'||a.st==='eat'){if(age>a.stMs){pause(a,t,800,2200);}}
  else if(a.st==='pause'){
    if(age>a.wait){
      const r=Math.random();
      if(r<0.30){enter(a,'eat',t,rnd(2000,3500));}
      else if(r<0.42&&!hushed(t)){enter(a,'hide',t,rnd(2500,4500));jiddoNotice(a,t);}
      else walkTo(a,fieldSpot(a.position,a.pad),t);
    }
  }else walking=stepToward(a,t,dt);
  settle(a,t,dt,walking);
}

// the rooster's brain: pause | walk | peck | flap | cry
// The second bird, and the loudest thing in the field — and the first of the
// fowl: the hen and the chick go the same way. He struts, pecks with the Eat
// clip, and flaps: the Fly clip for a moment with a little hop written under
// it, the same way the rabbit's arc is written, because the clip cannot lift
// him. The crow is the bray's shape: the cry fires, he stands up tall with his
// wings out under it (CLIPOF, animals.ts), and Jiddo turns round.
interface FowlOpts{snd:()=>void;ms:number;cryP:number;peckP:number;flapP:number;peckMs:[number,number]}
function fowl(a:Animal,t:number,dt:number,o:FowlOpts){
  const age=t-a.stT; let walking=false;
  if(a.st==='flap'){
    a.hopY=Math.sin(Math.min(1,age/a.stMs)*Math.PI)*a.spec.h*0.3;
    if(age>a.stMs){a.hopY=0;pause(a,t,500,1500);}
  }else if(a.st==='cry'||a.st==='peck'){if(age>a.stMs){pause(a,t,500,1500);}}
  else if(a.st==='pause'){
    if(age>a.wait){
      const r=Math.random();
      if(r<o.cryP&&!hushed(t)){cry(a,'cry',t,o.snd,o.ms);}
      else if(r<o.cryP+o.peckP){enter(a,'peck',t,rnd(o.peckMs[0],o.peckMs[1]));}
      else if(r<o.cryP+o.peckP+o.flapP){enter(a,'flap',t,700);}
      else walkTo(a,fieldSpot(a.position,a.pad),t);
    }
  }else walking=stepToward(a,t,dt);
  settle(a,t,dt,walking);
}
const ROOSTER:FowlOpts={snd:sndCrow,ms:1500,cryP:0.09,peckP:0.27,flapP:0.14,peckMs:[1500,2600]};   // rarer than a bray: a crow is long, and loud
function roosterBrain(a:Animal,t:number,dt:number){fowl(a,t,dt,ROOSTER);}

// the owl's brain: pause | walk | fly | cry
// The third bird, and the first animal that leaves the ground: she flies from
// one spot to another — and the eagle, the crow, the dove, the pigeon and the
// parrot fly the same way. The pack's Fly clip is a flap in place, so the
// flight is written the way the rabbit's hop is — a lerp across the field,
// eased at both ends so she launches and lands rather than slides, and a sine
// up into the air a body and a half high — with the clip flapping on the clock
// under it the whole way. Being airborne, she is the one walker who may cross
// the pond; she never lands on it, since fieldSpot never offers water. On the
// ground she hops along on the Walk clip, slowly, like the bird she is. The
// cry is the bray's shape: it fires, she stands up tall under it (CLIPOF,
// animals.ts), and Jiddo turns round.
interface FlyOpts{snd:()=>void;ms:number;cryP:number;flyP:number;high:number}
const FLYV=2.6;                                  // units a second, in the air
function startFlight(a:Animal,t:number){
  const p=a.position, s=fieldSpot(null,a.pad), d=Math.hypot(s.x-p.x,s.z-p.z);
  if(d<1.5){pause(a,t,400,1200);return;}   // not worth taking off for
  a.hx0=p.x; a.hz0=p.z; a.hx1=s.x; a.hz1=s.z;
  a.head=Math.atan2(s.x-p.x,s.z-p.z);
  enter(a,'fly',t,d/FLYV*1000);
}
function flier(a:Animal,t:number,dt:number,o:FlyOpts){
  const age=t-a.stT, p=a.position; let walking=false;
  if(a.st!=='fly')a.hopY=0;
  if(a.st==='fly'){
    const g=Math.min(1,age/a.stMs), e=g<0.5?2*g*g:1-2*(1-g)*(1-g);
    p.x=a.hx0+(a.hx1-a.hx0)*e; p.z=a.hz0+(a.hz1-a.hz0)*e;
    a.hopY=Math.sin(g*Math.PI)*a.spec.h*o.high;
    if(g>=1){a.hopY=0;pause(a,t,800,2400);}
  }else if(a.st==='cry'){if(age>a.stMs){pause(a,t,800,2400);}}
  else if(a.st==='pause'){
    if(age>a.wait){
      const r=Math.random();
      if(r<o.cryP&&!hushed(t)){cry(a,'cry',t,o.snd,o.ms);}
      else if(r<o.cryP+o.flyP)startFlight(a,t);
      else walkTo(a,fieldSpot(p,a.pad),t);
    }
  }else walking=stepToward(a,t,dt);
  settle(a,t,dt,walking);
}
const OWL:FlyOpts={snd:sndHoot,ms:1300,cryP:0.14,flyP:0.32,high:1.5};
function owlBrain(a:Animal,t:number,dt:number){flier(a,t,dt,OWL);}

// the fox's brain: pause | walk | stalk | pounce | sniff | yip | curl
// The one animal that takes an interest in the others — the first of them: the
// cat hunts the same way. Now and then he picks somebody in the field and
// creeps up on them — the Walk clip slowed, timed by the ground like any walk —
// and a couple of body lengths off he pounces: the Jump clip with an arc
// written under it, the rabbit's shape, that lands him just short. What
// happens next is the collisions' to sort out and the other animal's to answer
// (the tortoise hides); nobody is ever caught, and he sits back and thinks
// again. A stalk that runs long, or whose quarry has left the field, is given
// up. Otherwise he sniffs the grass with the Eat clip, curls up on the Sit
// clip, and yips the way the cow moos: the cry fires, he stands up tall under
// it (CLIPOF, animals.ts), and Jiddo turns round.
interface HuntOpts{snd:()=>void;ms:number;cryP:number;sniffP:number;curlP:number;stalkP:number;sniffMs:[number,number];curlMs:[number,number];gait?:'walk'|'dash'}
const POUNCED=520;
function startPounce(a:Animal,t:number,dx:number,dz:number,d:number){
  const p=a.position, reach=Math.max(0.3,Math.min(d-a.spec.h*0.5,a.spec.h*2.2)), x1=p.x+dx/d*reach, z1=p.z+dz/d*reach;
  if(crossesWater(p.x,p.z,x1,z1,a.pad)){a.prey=undefined;pause(a,t,500,1400);return;}   // not into the pond
  a.hx0=p.x; a.hz0=p.z; a.hx1=x1; a.hz1=z1; a.head=Math.atan2(dx,dz);
  a.st='pounce'; a.stT=t;
}
// a hunter: pause | walk | stalk | pounce | sniff | cry | curl
function hunter(a:Animal,t:number,dt:number,o:HuntOpts){
  const age=t-a.stT, p=a.position; let walking=false;
  if(a.st!=='pounce')a.hopY=0;
  a.slow=a.st==='stalk'?0.6:1;
  if(a.st==='pounce'){
    const g=Math.min(1,age/POUNCED);
    p.x=a.hx0+(a.hx1-a.hx0)*g; p.z=a.hz0+(a.hz1-a.hz0)*g;
    a.hopY=Math.sin(g*Math.PI)*a.spec.h*0.5;
    if(g>=1){a.hopY=0;a.prey=undefined;pause(a,t,700,1800);}
  }else if(a.st==='stalk'){
    const v=a.prey;
    if(!v||!animals.includes(v)||age>9000){a.prey=undefined;pause(a,t,500,1400);}
    else{
      a.tx=v.position.x; a.tz=v.position.z;
      const dx=a.tx-p.x, dz=a.tz-p.z, d=Math.hypot(dx,dz);
      if(d<a.spec.h*2.4)startPounce(a,t,dx,dz,d);
      else walking=stepToward(a,t,dt);              // its own stops apply: the bank, and the ten-second limit
    }
  }else if(a.st==='cry'||a.st==='sniff'||a.st==='curl'){if(age>a.stMs){pause(a,t,600,1700);}}
  else if(a.st==='pause'){
    if(age>a.wait){
      const r=Math.random(), others=animals.filter(v=>v!==a&&!(v.spec.swims&&onWater(v.position.x,v.position.z,0)));
      if(r<o.cryP&&!hushed(t)){cry(a,'cry',t,o.snd,o.ms);}
      else if(r<o.cryP+o.sniffP){enter(a,'sniff',t,rnd(o.sniffMs[0],o.sniffMs[1]));}
      else if(r<o.cryP+o.sniffP+o.curlP){enter(a,'curl',t,rnd(o.curlMs[0],o.curlMs[1]));}
      else if(r<o.cryP+o.sniffP+o.curlP+o.stalkP&&others.length){a.prey=others[(Math.random()*others.length)|0];a.st='stalk';a.stT=t;}
      else walkTo(a,fieldSpot(p,a.pad),t,o.gait);
    }
  }else walking=stepToward(a,t,dt);
  settle(a,t,dt,walking);
}
const FOX:HuntOpts={snd:sndYip,ms:900,cryP:0.12,sniffP:0.20,curlP:0.08,stalkP:0.26,sniffMs:[1500,2600],curlMs:[3500,6000]};
function foxBrain(a:Animal,t:number,dt:number){hunter(a,t,dt,FOX);}

// ---- the shapes the rest of the pack shares ----
// Most of the pack is one of a few animals with a different noise. From the
// sheep on, a brain that is another animal's shape is written once here and
// each animal's own function — the one BRAINS names, and check-words.js looks
// for — is one line naming its noise and its numbers. The cry is the state
// `cry` (Clicked, in CRIES), which the brain comment names in words.
interface HerdOpts{snd:()=>void;ms:number;grazeP:number;cryP:number;grazeMs:[number,number];rest?:Rest;pronkP?:number;gait?:'walk'|'dash'}
// a grazer: pause | walk | graze | cry — the donkey's shape — and, given a rest, the cow's rest on the Sit
// clip; given pronkP, a pronk: the Jump clip with a hop written straight up under it, which Jiddo remarks on
function herd(a:Animal,t:number,dt:number,o:HerdOpts){
  const age=t-a.stT; let walking=false;
  if(a.st!=='pronk')a.hopY=0;
  if(a.st==='pronk'){
    a.hopY=Math.sin(Math.min(1,age/a.stMs)*Math.PI)*a.spec.h*0.45;
    if(age>a.stMs){a.hopY=0;pause(a,t,420,1400);}
  }else if(a.st==='graze'||a.st==='cry'||a.st==='rest'){if(age>a.stMs){pause(a,t,420,1400);}}
  else if(a.st==='pause'){
    if(age>a.wait){
      const r=Math.random(), rest=o.rest, restP=rest?rest.p:0, pronkP=o.pronkP||0;
      if(r<o.grazeP){enter(a,'graze',t,rnd(o.grazeMs[0],o.grazeMs[1]));}
      else if(r<o.grazeP+o.cryP&&!hushed(t)){cry(a,'cry',t,o.snd,o.ms);}
      // A cry held for his voice (hushed) falls through to the next thing, as in
      // every brain; here that is the rest, which only some of the herd have —
      // a hushed sheep or reindeer once fell into a rest they had not got, the
      // frame threw, and the game stood still for good.
      else if(rest&&r<o.grazeP+o.cryP+restP){enter(a,'rest',t,rnd(rest.ms[0],rest.ms[1]));}
      else if(r<o.grazeP+o.cryP+restP+pronkP&&!hushed(t)){enter(a,'pronk',t,520);jiddoNotice(a,t);}
      else walkTo(a,fieldSpot(a.position,a.pad),t,o.gait);
    }
  }else walking=stepToward(a,t,dt);
  settle(a,t,dt,walking);
}
// the sheep: a grazer that baas
const SHEEP:HerdOpts={snd:sndBaa,ms:1000,grazeP:0.36,cryP:0.14,grazeMs:[2200,3600]};
function sheepBrain(a:Animal,t:number,dt:number){herd(a,t,dt,SHEEP);}

// the pig: a grazer that roots with the Eat clip, wallows on the Sit clip, and oinks
const PIG:HerdOpts={snd:sndOink,ms:800,grazeP:0.30,cryP:0.14,grazeMs:[1800,3200],rest:{p:0.14,ms:[3000,6000]}};
function pigBrain(a:Animal,t:number,dt:number){herd(a,t,dt,PIG);}

interface DartOpts{snd:()=>void;ms:number;sniffP:number;cryP:number;sniffMs:[number,number];dash:[number,number]}
// a darter: pause | dash | sniff | cry — the mouse's shape: short dashes on the
// Run clip, timed by the ground like any walk, a sniff of the grass between them
function darter(a:Animal,t:number,dt:number,o:DartOpts){
  const age=t-a.stT, p=a.position; let walking=false;
  if(a.st==='sniff'||a.st==='cry'){if(age>a.stMs){pause(a,t,300,1200);}}
  else if(a.st==='pause'){
    if(age>a.wait){
      const r=Math.random();
      if(r<o.sniffP){enter(a,'sniff',t,rnd(o.sniffMs[0],o.sniffMs[1]));}
      else if(r<o.sniffP+o.cryP&&!hushed(t)){cry(a,'cry',t,o.snd,o.ms);}
      else{                                       // a spot, but only part of the way to it: a dash, then a think
        const s=fieldSpot(p,a.pad), dx=s.x-p.x, dz=s.z-p.z, d=Math.hypot(dx,dz)||1, k=Math.min(1,rnd(o.dash[0],o.dash[1])/d);
        walkTo(a,{x:p.x+dx*k,z:p.z+dz*k},t,'dash');
      }
    }
  }else walking=stepToward(a,t,dt);
  settle(a,t,dt,walking);
}
// the mouse: the smallest thing in the field, and the quickest over a short way
const MOUSE:DartOpts={snd:sndSqueak,ms:800,sniffP:0.34,cryP:0.12,sniffMs:[900,1800],dash:[1.5,3.5]};
function mouseBrain(a:Animal,t:number,dt:number){darter(a,t,dt,MOUSE);}

// the cat: the fox's shape, sleepier — he hunts the same way, and the mouse is in the field now
const CAT:HuntOpts={snd:sndMeow,ms:900,cryP:0.14,sniffP:0.12,curlP:0.30,stalkP:0.24,sniffMs:[1200,2200],curlMs:[5000,9000]};
function catBrain(a:Animal,t:number,dt:number){hunter(a,t,dt,CAT);}

// the dog's brain: pause | walk | follow | rest | cry
// The one animal who is interested in Jiddo rather than in the others: now and
// then he goes to him — the Walk clip, timed by the ground — stops a stride and
// a half off, and sits by him on the Sit clip for a while before he wanders
// again. He barks the way the cow moos.
const DOGNEAR=1.8;
function dogBrain(a:Animal,t:number,dt:number){
  const age=t-a.stT, p=a.position; let walking=false;
  if(a.st==='follow'){
    a.tx=jiddo.position.x; a.tz=jiddo.position.z;
    if(Math.hypot(a.tx-p.x,a.tz-p.z)<DOGNEAR){enter(a,'rest',t,rnd(3000,6000));a.head=Math.atan2(a.tx-p.x,a.tz-p.z);}
    else if(age>8000){pause(a,t,500,1400);}   // he is not coming: give up
    else walking=stepToward(a,t,dt);
  }else if(a.st==='rest'||a.st==='cry'){if(age>a.stMs){pause(a,t,500,1500);}}
  else if(a.st==='pause'){
    if(age>a.wait){
      const r=Math.random();
      if(r<0.14&&!hushed(t)){cry(a,'cry',t,sndBark,800);}
      else if(r<0.44&&Math.hypot(jiddo.position.x-p.x,jiddo.position.z-p.z)>DOGNEAR+0.5){a.st='follow';a.stT=t;}
      else walkTo(a,fieldSpot(p,a.pad),t);
    }
  }else walking=stepToward(a,t,dt);
  settle(a,t,dt,walking);
}

// the hen: the rooster's shape, quieter — she pecks more, and clucks
const HEN:FowlOpts={snd:sndCluck,ms:900,cryP:0.12,peckP:0.36,flapP:0.10,peckMs:[1500,2800]};
function henBrain(a:Animal,t:number,dt:number){fowl(a,t,dt,HEN);}

interface DrinkOpts{snd:()=>void;ms:number;cryP:number;drinkP:number;pondP:number;drinkMs:[number,number]}
// a drinker: pause | walk | drink | cry — the elephant's shape without the blubs:
// often it heads for the bank on its own side of the pond (drinkSpot), and
// close to the water it puts its head down to drink with the Eat clip
function drinker(a:Animal,t:number,dt:number,o:DrinkOpts){
  const age=t-a.stT, p=a.position; let walking=false;
  if(a.st==='drink'||a.st==='cry'){if(age>a.stMs){pause(a,t,600,1900);}}
  else if(a.st==='pause'){
    if(age>a.wait){
      const r=Math.random(), atPond=pondR(p.x,p.z,a.pad)<1.35;
      if(r<o.cryP&&!hushed(t)){cry(a,'cry',t,o.snd,o.ms);}
      else if(atPond&&r<o.cryP+o.drinkP){enter(a,'drink',t,rnd(o.drinkMs[0],o.drinkMs[1]));a.head=Math.atan2(pond.x-p.x,pond.z-p.z);}
      else{const s=(Math.random()<o.pondP&&drinkSpot(a))||fieldSpot(p,a.pad);walkTo(a,s,t);}
    }
  }else walking=stepToward(a,t,dt);
  settle(a,t,dt,walking);
}
// the polar bear: the biggest thing in the field after the elephant, and the other one who goes to the water
const BEAR:DrinkOpts={snd:sndGrowl,ms:1300,cryP:0.14,drinkP:0.6,pondP:0.4,drinkMs:[1800,3200]};
function bearBrain(a:Animal,t:number,dt:number){drinker(a,t,dt,BEAR);}

// the goldfish's brain: float | swim | jump
// The one animal that never touches the grass: her spec says `water`, so she
// arrives on the pond, and every spot she picks is another spot on it. She
// swims between them on the Swim clip, let down into the water further than
// the duck, floats in between, and now and then jumps — the Jump clip with the
// rabbit's arc written straight up, and a blub — which is what Jiddo remarks
// on; she has no cry. Shoved onto the bank by a bump, she swims straight back.
function fishBrain(a:Animal,t:number,dt:number){
  const age=t-a.stT, p=a.position;
  a.dip=-0.12; a.slow=0.6;
  if(a.st!=='jump')a.hopY=0;
  if(a.st==='jump'){
    const g=Math.min(1,age/520); a.hopY=Math.sin(g*Math.PI)*a.spec.h*1.2;
    if(g>=1){a.hopY=0;float(a,t,800,2200);}
  }else if(a.st==='float'){
    if(age>a.wait){
      if(Math.random()<0.14&&!hushed(t)){a.st='jump';a.stT=t;sndBlub();jiddoNotice(a,t);}
      else walkTo(a,pondSpot(),t,'swim');
    }
  }else if(a.st==='swim'){stepToward(a,t,dt); if(a.st!=='swim'){float(a,t,500,1500);}}
  else{float(a,t,500,1500);}                          // an arrival's pause, a bump: float
  if(a.st!=='swim'&&a.st!=='jump'&&!onWater(p.x,p.z,0))walkTo(a,pondSpot(),t,'swim');   // back into the water
  settle(a,t,dt,false);
}

// the gazelle: a grazer that pronks — straight up, all four feet off the ground — and is silent otherwise
const GAZELLE:HerdOpts={snd:()=>{},ms:0,grazeP:0.32,cryP:0,grazeMs:[1800,3200],pronkP:0.18};
function gazelleBrain(a:Animal,t:number,dt:number){herd(a,t,dt,GAZELLE);}

// the chick: the smallest fowl, who goes and stands by the hen when she is in
// the field — the dog's follow, aimed at her — and peeps
const CHICK:FowlOpts={snd:sndCheep,ms:900,cryP:0.14,peckP:0.30,flapP:0.10,peckMs:[1000,2000]};
function chickBrain(a:Animal,t:number,dt:number){
  const age=t-a.stT, p=a.position;
  if(a.st==='follow'){
    const v=a.prey;
    if(!v||!animals.includes(v)||age>8000){a.prey=undefined;pause(a,t,400,1200);}
    else{
      a.tx=v.position.x; a.tz=v.position.z;
      if(Math.hypot(a.tx-p.x,a.tz-p.z)<1.1){a.prey=undefined;pause(a,t,1500,3500);}
      else{settle(a,t,dt,stepToward(a,t,dt));return;}
    }
    settle(a,t,dt,false);return;
  }
  if(a.st==='pause'&&age>a.wait&&Math.random()<0.3){
    const hen=animals.find(v=>v.w==='دجاجة');
    if(hen&&Math.hypot(hen.position.x-p.x,hen.position.z-p.z)>1.6){a.prey=hen;a.st='follow';a.stT=t;settle(a,t,dt,false);return;}
  }
  fowl(a,t,dt,CHICK);
}

// the wolf: the fox's shape, bigger and bolder — he stalks more and howls
const WOLF:HuntOpts={snd:sndHowl,ms:1600,cryP:0.12,sniffP:0.14,curlP:0.10,stalkP:0.34,sniffMs:[1200,2200],curlMs:[3000,5000]};
function wolfBrain(a:Animal,t:number,dt:number){hunter(a,t,dt,WOLF);}

// the ox: the cow's shape, heavier — he grazes, rests and bellows
const OX:HerdOpts={snd:sndBellow,ms:1100,grazeP:0.36,cryP:0.12,grazeMs:[2500,4000],rest:{p:0.12,ms:[4000,7000]}};
function oxBrain(a:Animal,t:number,dt:number){herd(a,t,dt,OX);}

// the buffalo: the ox's shape again, and he snorts
const BUFFALO:HerdOpts={snd:sndSnort,ms:1200,grazeP:0.36,cryP:0.12,grazeMs:[2500,4000],rest:{p:0.12,ms:[4000,7000]}};
function buffaloBrain(a:Animal,t:number,dt:number){herd(a,t,dt,BUFFALO);}

// the reindeer: a grazer who grunts, and pronks now and then like the gazelle
const REINDEER:HerdOpts={snd:sndGrunt,ms:1100,grazeP:0.34,cryP:0.12,grazeMs:[2000,3500],pronkP:0.08};
function reindeerBrain(a:Animal,t:number,dt:number){herd(a,t,dt,REINDEER);}

// the cheetah: the fox's shape at a run — he wanders and stalks on the Run
// clip, the fastest thing in the field — and he chirps, which is what a cheetah
// really does. No recording of one is free to use, so the chirp stays written
const CHEETAH:HuntOpts={snd:sndChirp,ms:700,cryP:0.12,sniffP:0.12,curlP:0.16,stalkP:0.30,sniffMs:[1000,2000],curlMs:[3000,6000],gait:'dash'};
function cheetahBrain(a:Animal,t:number,dt:number){hunter(a,t,dt,CHEETAH);}

// the hyena: the fox's shape, and he laughs
const HYENA:HuntOpts={snd:sndLaugh,ms:1500,cryP:0.16,sniffP:0.16,curlP:0.10,stalkP:0.28,sniffMs:[1200,2400],curlMs:[3000,5500]};
function hyenaBrain(a:Animal,t:number,dt:number){hunter(a,t,dt,HYENA);}

// the eagle: the owl's shape, higher and further — she flies more than she walks, and screeches
const EAGLE:FlyOpts={snd:sndScreech,ms:1400,cryP:0.12,flyP:0.46,high:2.2};
function eagleBrain(a:Animal,t:number,dt:number){flier(a,t,dt,EAGLE);}

// the crow: the owl's shape, and he caws
const CROW:FlyOpts={snd:sndCaw,ms:1800,cryP:0.16,flyP:0.34,high:1.6};
function crowBrain(a:Animal,t:number,dt:number){flier(a,t,dt,CROW);}

// the dove: the owl's shape, and she coos
const DOVE:FlyOpts={snd:sndCoo,ms:2000,cryP:0.14,flyP:0.34,high:1.5};
function doveBrain(a:Animal,t:number,dt:number){flier(a,t,dt,DOVE);}

// the pigeon: the dove again, on the ground more, and she coos too
const PIGEON:FlyOpts={snd:sndCoo,ms:1500,cryP:0.14,flyP:0.26,high:1.4};
function pigeonBrain(a:Animal,t:number,dt:number){flier(a,t,dt,PIGEON);}

// the parrot: the owl's shape, and he squawks — written, since the only free
// recording of one is a man saying Polly wants a cracker
const PARROT:FlyOpts={snd:sndSquawk,ms:900,cryP:0.18,flyP:0.30,high:1.5};
function parrotBrain(a:Animal,t:number,dt:number){flier(a,t,dt,PARROT);}

// the ostrich: the tallest bird, and the one that runs — a grazer on the Run
// clip, pecking the ground with Eat, and silent; the run is what Jiddo remarks on
const OSTRICH:HerdOpts={snd:()=>{},ms:0,grazeP:0.30,cryP:0,grazeMs:[1500,2800],gait:'dash'};
function ostrichBrain(a:Animal,t:number,dt:number){herd(a,t,dt,OSTRICH);}

// the penguin: the duck's shape — the swimmer is written once now — with a
// waddle on land, more time in the water, and a squeak
const PENGUIN:SwimOpts={snd:sndPeng,ms:800,cryP:0.14,eatP:0.14,toWater:0.7,toLand:0.3,dip:-0.06,cry:'cry'};
function penguinBrain(a:Animal,t:number,dt:number){swimmer(a,t,dt,PENGUIN);}

// the sea lion: a swimmer who flops along the grass on her flippers, lies on
// the bank on the Sit clip, and barks. Her name is the Levantine فقمة, one word,
// where the book's أسد البحر is two
const SEALION:SwimOpts={snd:sndArf,ms:900,cryP:0.14,eatP:0.08,toWater:0.6,toLand:0.35,dip:-0.1,cry:'cry',rest:{p:0.3,ms:[3000,6000]}};
function sealionBrain(a:Animal,t:number,dt:number){swimmer(a,t,dt,SEALION);}

// the walrus: the sea lion again, slower and lower in the water, who grumbles —
// written, since no recording of one is free to use
const WALRUS:SwimOpts={snd:sndWalrus,ms:1200,cryP:0.12,eatP:0.08,toWater:0.55,toLand:0.35,dip:-0.14,cry:'cry',rest:{p:0.36,ms:[4000,8000]}};
function walrusBrain(a:Animal,t:number,dt:number){swimmer(a,t,dt,WALRUS);}

// the raccoon: the bear's shape in miniature — he goes to the water often, and
// what he does there with the Eat clip is washing — and he chitters
const RACCOON:DrinkOpts={snd:sndChitter,ms:900,cryP:0.14,drinkP:0.55,pondP:0.5,drinkMs:[2000,3500]};
function raccoonBrain(a:Animal,t:number,dt:number){drinker(a,t,dt,RACCOON);}

// the rabbit's brain: pause | hop | land | eat
// He moves in discrete hops rather than sliding at a constant speed, which is
// what actually makes him read as alive. The pack has a Jump clip for the
// airborne pose, but a clip with no root motion cannot carry him anywhere, so
// the arc between the two planted frames is still ours: one hop is a lerp
// across the ground and half a sine into the air. He takes several to cross the
// field, thinking again at each landing.
const HOPD=420, HOPGAP=150;
function startHop(a:Animal,t:number){
  const p=a.position, dx=a.tx-p.x, dz=a.tz-p.z, d=Math.hypot(dx,dz)||1;
  const reach=Math.min(d,a.spec.h*rnd(1.6,2.6)), x1=p.x+dx/d*reach, z1=p.z+dz/d*reach;
  if(crossesWater(p.x,p.z,x1,z1,a.pad)){pause(a,t,400,1500);return;}   // not into the pond: think again
  a.hx0=p.x; a.hz0=p.z; a.hx1=x1; a.hz1=z1;
  a.head=Math.atan2(dx,dz);
  a.st='hop'; a.stT=t;
}
function rabbitBrain(a:Animal,t:number,dt:number){
  const age=t-a.stT, p=a.position;
  if(a.st!=='hop')a.hopY=0;                        // a hop cut short by a bump lands him
  if(a.st==='hop'){
    const g=Math.min(1,age/HOPD);
    p.x=a.hx0+(a.hx1-a.hx0)*g; p.z=a.hz0+(a.hz1-a.hz0)*g;
    a.hopY=Math.sin(g*Math.PI)*a.spec.h*0.45;
    if(g>=1){a.hopY=0;a.st='land';a.stT=t;}
  }else if(a.st==='land'){
    if(age>HOPGAP){
      if(Math.hypot(a.tx-p.x,a.tz-p.z)<0.4){pause(a,t,400,1500);}
      else startHop(a,t);
    }
  }else if(a.st==='eat'){
    if(age>1700){pause(a,t,400,1500);}
  }else if(age>a.wait){
    if(Math.random()<0.30){a.st='eat';a.stT=t;}
    else{const s=fieldSpot(p,a.pad);a.tx=s.x;a.tz=s.z;startHop(a,t);}
  }
  settle(a,t,dt,false);
}

// The registry: what a row in data.ts means by brain:'elephant'. A new brain
// goes in here, and check-words.js reads this block to see that every row
// names one.
export const BRAINS={elephant:elephantBrain,donkey:donkeyBrain,cow:cowBrain,duck:duckBrain,snake:snakeBrain,
  tortoise:tortoiseBrain,rooster:roosterBrain,owl:owlBrain,fox:foxBrain,sheep:sheepBrain,pig:pigBrain,mouse:mouseBrain,cat:catBrain,dog:dogBrain,hen:henBrain,bear:bearBrain,fish:fishBrain,gazelle:gazelleBrain,chick:chickBrain,wolf:wolfBrain,ox:oxBrain,buffalo:buffaloBrain,reindeer:reindeerBrain,cheetah:cheetahBrain,hyena:hyenaBrain,eagle:eagleBrain,crow:crowBrain,dove:doveBrain,pigeon:pigeonBrain,parrot:parrotBrain,ostrich:ostrichBrain,penguin:penguinBrain,sealion:sealionBrain,walrus:walrusBrain,raccoon:raccoonBrain,rabbit:rabbitBrain} satisfies Record<string,Brain>;
export type BrainName=keyof typeof BRAINS;
